/**
 * Catalog data fetching and state management hook
 * 
 * IMPORTANT: Uses useAuth() hook to read cached auth state.
 * DO NOT call supabase.auth.getUser() here — it causes auth loops!
 * 
 * Includes IndexedDB offline fallback for catalog browsing without internet.
 * Pre-caches images for offline support.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { rpcMainBootstrap, rpcCatalogSearchV2 } from '@/lib/api';
import type { MainBootstrapViewer, ListingCard, CatalogSearchItem } from '@/lib/api';
import { createTraceId, logger } from '@/lib/services';
import { useCatalogCache } from '@/hooks/useCatalogCache';
import { useAuth } from '@/hooks/useAuth';
import { useUserContext } from '@/contexts/UserContext';
import { cacheConfig, getCachedData, setCacheData } from '@/lib/config/cacheConfig';
import { saveCatalogToIDB, loadCatalogFromIDB } from '@/lib/services/catalogOfflineService';
import { isOnline } from '@/hooks/useOnlineStatus';
import { preloadImages } from '@/components/ui/CachedImage';

const SCREEN_ID = 'TAB-001';
const DEFAULT_LIMIT = 24;

/**
 * Normalize asset paths coming from backend.
 */
export function normalizeAssetUrl(raw?: string | null): string | null {
  if (!raw) return null;

  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw;
  }

  if (raw.startsWith('public/')) {
    return '/' + raw.slice('public/'.length);
  }

  if (raw.startsWith('assets/')) {
    return '/' + raw;
  }

  if (!raw.startsWith('/')) {
    return '/' + raw;
  }

  return raw;
}

/**
 * Convert CatalogSearchItem to ListingCard format
 */
export function mapSearchItemToListingCard(item: CatalogSearchItem): ListingCard {
  const primaryUrl = normalizeAssetUrl(item.primary_media_url);
  const normalizedPhotos = item.photos && item.photos.length > 0
    ? item.photos.map(url => normalizeAssetUrl(url)).filter((url): url is string => url !== null)
    : (primaryUrl ? [primaryUrl] : []);

  return {
    listing_id: item.listing_id,
    pet_id: item.pet_id,
    pet_name: item.pet_name ?? 'Без имени',
    pet_description: item.pet_description,
    interesting_fact: item.interesting_fact,
    species_key: item.species_key,
    breed_name: item.breed_name,
    age_bucket: item.age_bucket,
    sex: item.sex,
    city_name: item.geo_place_name,
    city: item.geo_place_id && item.geo_place_name ? {
      id: item.geo_place_id,
      name: item.geo_place_name,
    } : undefined,
    primary_photo_url: primaryUrl,
    photos: normalizedPhotos,
    is_favorited: false,
    published_at: item.published_at ?? item.listing_created_at,
    likes_count: item.likes_count,
  };
}

interface CatalogFilters {
  speciesKey: string | null;
  breedIds: string[];
  sex: string[];
  ageBuckets: string[];
  onlyWithPhotos: boolean;
  sort: 'created_at:desc' | 'created_at:asc' | 'age:young' | 'age:old' | 'popularity:desc';
  // Extended filters (v2)
  sizes: string[];
  colors: string[];
  healthSpecialNeeds: boolean | null;
  characterTypes: string[];
}

type SortType = 'created_at:desc' | 'created_at:asc' | 'age:young' | 'age:old' | 'popularity:desc';

interface QuizFilters {
  speciesKey: string | null;
  breedIds: string[];
  sex: string[];
  ageBuckets: string[];
}

interface UseCatalogDataParams {
  filters: CatalogFilters;
  selectedCityId: string | null;
  searchQuery: string;
  isForYouMode: boolean;
  effectiveSort: SortType;
}

export function useCatalogData({
  filters,
  selectedCityId,
  searchQuery,
  isForYouMode,
  effectiveSort,
}: UseCatalogDataParams) {
  const { shouldFetch, getCached, updateCache, getFilterKey } = useCatalogCache();
  
  // Get user and quiz profile from context — no additional network calls!
  const { user } = useAuth();
  const { quizProfile } = useUserContext();

  const [viewer, setViewer] = useState<MainBootstrapViewer | null>(null);
  const [catalogItems, setCatalogItems] = useState<ListingCard[]>([]);
  const [catalogHasMore, setCatalogHasMore] = useState(false);
  const [lastCursor, setLastCursor] = useState<{ createdAt: string; listingId: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);

  // Convert UserContext QuizProfile to local QuizFilters format
  // CRITICAL: Must be memoized to prevent infinite useEffect loops!
  const quizFilters = useMemo<QuizFilters | null>(() => {
    if (!quizProfile) return null;
    return {
      speciesKey: quizProfile.speciesKey,
      breedIds: quizProfile.breedIds,
      sex: [],
      ageBuckets: [],
    };
  }, [quizProfile?.speciesKey, quizProfile?.breedIds]);

  // Memoize filterKey to prevent unnecessary recalculations
  const filterKey = useMemo(() => getFilterKey({
    speciesKey: filters.speciesKey,
    breedIds: filters.breedIds,
    sex: filters.sex,
    ageBuckets: filters.ageBuckets,
    geoPlaceId: selectedCityId,
    searchQuery,
    sort: effectiveSort,
    isForYouMode,
  }), [filters.speciesKey, filters.breedIds, filters.sex, filters.ageBuckets, selectedCityId, searchQuery, effectiveSort, isForYouMode]);

  // Fetch viewer data on mount with caching
  useEffect(() => {
    const { ttlMs, storageKey } = cacheConfig.viewer;
    
    // Try to get cached viewer first
    const cachedViewer = getCachedData<MainBootstrapViewer>(storageKey, ttlMs);
    if (cachedViewer) {
      setViewer(cachedViewer);
      return;
    }
    
    const fetchViewer = async () => {
      try {
        const payload = await rpcMainBootstrap({
          city_id: null,
          page: 1,
          limit: 1,
          sort_field: 'published_at',
          sort_dir: 'desc',
        });
        setViewer(payload.viewer);
        // Cache the viewer data
        setCacheData(storageKey, payload.viewer);
      } catch {
        // Silent fail for viewer
      }
    };
    fetchViewer();
  }, []);

  // Quiz filters are now loaded from UserContext (cached)

  // Main data fetch with filters and caching (including IndexedDB offline)
  useEffect(() => {
    let cancelled = false;
    const traceId = createTraceId();

    const fetchData = async () => {
      // Check React Query cache first
      const cached = getCached(filterKey);
      if (cached && !shouldFetch(filterKey)) {
        setCatalogItems(cached.items);
        setCatalogHasMore(cached.hasMore);
        setLastCursor(cached.cursor);
        setIsLoading(false);
        setIsFromCache(false);
        return;
      }

      // Try IndexedDB for instant display
      const idbCached = await loadCatalogFromIDB(filterKey);
      if (idbCached) {
        setCatalogItems(idbCached.items as ListingCard[]);
        setCatalogHasMore(idbCached.hasMore);
        setLastCursor(idbCached.cursor);
        setIsFromCache(true);
        // Don't set loading false yet - we'll try to fetch fresh data
      }

      // If offline, stop here with cached data
      if (!isOnline()) {
        if (!idbCached) {
          setError('Нет подключения к интернету');
        }
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      setLastCursor(null);

      // Determine effective filters based on mode
      const effectiveSpecies = isForYouMode && quizFilters?.speciesKey 
        ? quizFilters.speciesKey 
        : (isForYouMode ? null : filters.speciesKey);
      const effectiveBreeds = isForYouMode && quizFilters?.breedIds.length 
        ? quizFilters.breedIds 
        : (isForYouMode ? null : (filters.breedIds.length > 0 ? filters.breedIds : null));
      const effectiveSex = isForYouMode ? null : (filters.sex.length > 0 ? filters.sex : null);
      const effectiveAgeBuckets = isForYouMode ? null : (filters.ageBuckets.length > 0 ? filters.ageBuckets : null);

      logger.info('Catalog fetch started', {
        context: { feature: 'catalog', function_name: 'fetchData', screen_id: SCREEN_ID, trace_id: traceId },
        data: { filters, selectedCityId, searchQuery, isForYouMode, hasQuizFilters: !!quizFilters },
        allowList: ['filters', 'selectedCityId', 'searchQuery', 'isForYouMode', 'hasQuizFilters'],
      });

      try {
        const items = await rpcCatalogSearchV2({
          species_key: effectiveSpecies,
          breed_ids: effectiveBreeds,
          sex: effectiveSex,
          age_buckets: effectiveAgeBuckets,
          geo_place_id: selectedCityId,
          text_search: searchQuery || null,
          only_with_photos: isForYouMode ? false : filters.onlyWithPhotos,
          sort: effectiveSort,
          limit: DEFAULT_LIMIT,
          // Extended filters (v2) - only apply in regular mode, not "For You"
          sizes: isForYouMode ? null : ((filters.sizes?.length ?? 0) > 0 ? filters.sizes : null),
          colors: isForYouMode ? null : ((filters.colors?.length ?? 0) > 0 ? filters.colors : null),
          health_special_needs: isForYouMode ? null : filters.healthSpecialNeeds,
          character_types: isForYouMode ? null : ((filters.characterTypes?.length ?? 0) > 0 ? filters.characterTypes : null),
        });

        if (cancelled) return;

        const mappedItems = items.map(mapSearchItemToListingCard);
        setCatalogItems(mappedItems);
        setCatalogHasMore(items.length === DEFAULT_LIMIT);
        setIsFromCache(false);

        const newCursor = items.length > 0 ? {
          createdAt: items[items.length - 1].listing_created_at,
          listingId: items[items.length - 1].listing_id,
        } : null;

        if (newCursor) {
          setLastCursor(newCursor);
        }

        // Pre-cache images for offline support (non-blocking)
        const allImageUrls = mappedItems.flatMap(item => item.photos || []).filter(Boolean);
        if (allImageUrls.length > 0) {
          preloadImages(allImageUrls).catch(() => {});
        }

        // Save to IndexedDB for offline access
        saveCatalogToIDB(filterKey, mappedItems, items.length === DEFAULT_LIMIT, newCursor).catch(() => {});

        logger.info('Catalog fetch success', {
          context: { feature: 'catalog', function_name: 'fetchData', screen_id: SCREEN_ID, trace_id: traceId },
          data: { itemsCount: items.length, hasMore: items.length === DEFAULT_LIMIT },
          allowList: ['itemsCount', 'hasMore'],
        });
      } catch (e: unknown) {
        if (!cancelled) {
          // Only show error if we don't have cached data
          if (!idbCached) {
            const message = e instanceof Error ? e.message : 'Не удалось загрузить данные';
            setError(message);
          }
          logger.error('Catalog fetch error', {
            context: { feature: 'catalog', function_name: 'fetchData', screen_id: SCREEN_ID, trace_id: traceId },
            error: e,
            error_code: 'catalog_fetch_failed',
            error_type: 'network',
          });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [filters, selectedCityId, searchQuery, isForYouMode, effectiveSort, filterKey, getCached, shouldFetch, quizFilters]);

  // Load more function for pagination
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !catalogHasMore || !lastCursor) return;

    const traceId = createTraceId();
    setIsLoadingMore(true);

    // Determine effective filters based on mode
    const effectiveSpecies = isForYouMode && quizFilters?.speciesKey 
      ? quizFilters.speciesKey 
      : (isForYouMode ? null : filters.speciesKey);
    const effectiveBreeds = isForYouMode && quizFilters?.breedIds.length 
      ? quizFilters.breedIds 
      : (isForYouMode ? null : (filters.breedIds.length > 0 ? filters.breedIds : null));
    const effectiveSex = isForYouMode ? null : (filters.sex.length > 0 ? filters.sex : null);
    const effectiveAgeBuckets = isForYouMode ? null : (filters.ageBuckets.length > 0 ? filters.ageBuckets : null);

    try {
      const items = await rpcCatalogSearchV2({
        species_key: effectiveSpecies,
        breed_ids: effectiveBreeds,
        sex: effectiveSex,
        age_buckets: effectiveAgeBuckets,
        geo_place_id: selectedCityId,
        text_search: searchQuery || null,
        only_with_photos: isForYouMode ? false : filters.onlyWithPhotos,
        sort: effectiveSort,
        limit: DEFAULT_LIMIT,
        cursor_created_at: lastCursor.createdAt,
        cursor_listing_id: lastCursor.listingId,
        // Extended filters (v2)
        sizes: isForYouMode ? null : ((filters.sizes?.length ?? 0) > 0 ? filters.sizes : null),
        colors: isForYouMode ? null : ((filters.colors?.length ?? 0) > 0 ? filters.colors : null),
        health_special_needs: isForYouMode ? null : filters.healthSpecialNeeds,
        character_types: isForYouMode ? null : ((filters.characterTypes?.length ?? 0) > 0 ? filters.characterTypes : null),
      });

      const mappedItems = items.map(mapSearchItemToListingCard);
      setCatalogItems(prev => [...prev, ...mappedItems]);
      setCatalogHasMore(items.length === DEFAULT_LIMIT);

      if (items.length > 0) {
        const lastItem = items[items.length - 1];
        setLastCursor({
          createdAt: lastItem.listing_created_at,
          listingId: lastItem.listing_id,
        });
      }
    } catch (e: unknown) {
      logger.error('Failed to load more:', {
        context: { feature: 'catalog', function_name: 'handleLoadMore', screen_id: SCREEN_ID, trace_id: traceId },
        error: e,
        error_code: 'catalog_load_more_failed',
        error_type: 'network',
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, catalogHasMore, lastCursor, filters, selectedCityId, searchQuery, isForYouMode, effectiveSort, quizFilters]);

  // Retry function
  const handleRetry = useCallback(() => {
    setIsLoading(true);
    setError(null);
    setCatalogItems([]);
    setLastCursor(null);
    setCatalogHasMore(false);

    rpcCatalogSearchV2({
      species_key: isForYouMode ? null : filters.speciesKey,
      breed_ids: isForYouMode ? null : (filters.breedIds.length > 0 ? filters.breedIds : null),
      sex: isForYouMode ? null : (filters.sex.length > 0 ? filters.sex : null),
      age_buckets: isForYouMode ? null : (filters.ageBuckets.length > 0 ? filters.ageBuckets : null),
      geo_place_id: selectedCityId,
      text_search: searchQuery || null,
      only_with_photos: isForYouMode ? false : filters.onlyWithPhotos,
      sort: effectiveSort,
      limit: DEFAULT_LIMIT,
        // Extended filters (v2)
        sizes: isForYouMode ? null : ((filters.sizes?.length ?? 0) > 0 ? filters.sizes : null),
        colors: isForYouMode ? null : ((filters.colors?.length ?? 0) > 0 ? filters.colors : null),
        health_special_needs: isForYouMode ? null : filters.healthSpecialNeeds,
        character_types: isForYouMode ? null : ((filters.characterTypes?.length ?? 0) > 0 ? filters.characterTypes : null),
      })
      .then(items => {
        const mappedItems = items.map(mapSearchItemToListingCard);
        setCatalogItems(mappedItems);
        setCatalogHasMore(items.length === DEFAULT_LIMIT);

        if (items.length > 0) {
          const lastItem = items[items.length - 1];
          setLastCursor({
            createdAt: lastItem.listing_created_at,
            listingId: lastItem.listing_id,
          });
        }
      })
      .catch(e => {
        const message = e instanceof Error ? e.message : 'Не удалось загрузить данные';
        setError(message);
        logger.error('Catalog retry failed', {
          context: { feature: 'catalog', function_name: 'handleRetry', screen_id: SCREEN_ID },
          error: e,
          error_code: 'catalog_retry_failed',
          error_type: 'network',
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filters, selectedCityId, searchQuery, isForYouMode, effectiveSort]);

  // Refresh function for pull-to-refresh
  const handleRefresh = useCallback(async () => {
    setError(null);
    setLastCursor(null);

    try {
      const items = await rpcCatalogSearchV2({
        species_key: isForYouMode ? null : filters.speciesKey,
        breed_ids: isForYouMode ? null : (filters.breedIds.length > 0 ? filters.breedIds : null),
        sex: isForYouMode ? null : (filters.sex.length > 0 ? filters.sex : null),
        age_buckets: isForYouMode ? null : (filters.ageBuckets.length > 0 ? filters.ageBuckets : null),
        geo_place_id: selectedCityId,
        text_search: searchQuery || null,
        only_with_photos: isForYouMode ? false : filters.onlyWithPhotos,
        sort: effectiveSort,
        limit: DEFAULT_LIMIT,
        // Extended filters (v2)
        sizes: isForYouMode ? null : ((filters.sizes?.length ?? 0) > 0 ? filters.sizes : null),
        colors: isForYouMode ? null : ((filters.colors?.length ?? 0) > 0 ? filters.colors : null),
        health_special_needs: isForYouMode ? null : filters.healthSpecialNeeds,
        character_types: isForYouMode ? null : ((filters.characterTypes?.length ?? 0) > 0 ? filters.characterTypes : null),
      });

      const mappedItems = items.map(mapSearchItemToListingCard);
      setCatalogItems(mappedItems);
      setCatalogHasMore(items.length === DEFAULT_LIMIT);

      if (items.length > 0) {
        const lastItem = items[items.length - 1];
        setLastCursor({
          createdAt: lastItem.listing_created_at,
          listingId: lastItem.listing_id,
        });
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Не удалось загрузить данные';
      setError(message);
    }
  }, [filters, selectedCityId, searchQuery, isForYouMode, effectiveSort]);

  return {
    viewer,
    catalogItems,
    catalogHasMore,
    isLoading,
    isLoadingMore,
    error,
    isFromCache,
    handleLoadMore,
    handleRetry,
    handleRefresh,
  };
}
